'use client';

import { c as _c } from "react/compiler-runtime";
import './volunteer-slot-card.scss';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '@sola/ark-core/molecules/tooltip/tooltip.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { GraphQLVolunteerStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { type SlotCardContactInput, getVolunteerSlotCardData } from './volunteer-slot-card.logic.ts';
interface ConfirmTooltipProps extends PropsWithChildren {
  disabled: boolean;
  status?: GraphQLVolunteerStatus | null;
}
export type VolunteerSlotCardProps = {
  startTime: Date | string;
  endTime: Date | string;
  title: string;
  status?: GraphQLVolunteerStatus | null;
  contacts: SlotCardContactInput[];
  className?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
};
const ConfirmTooltip: React.FC<ConfirmTooltipProps> = t0 => {
  const $ = _c(10);
  const {
    children,
    disabled,
    status
  } = t0;
  const {
    t
  } = useTranslation("volunteers");
  if (disabled) {
    return children;
  }
  let t1;
  if ($[0] !== status || $[1] !== t) {
    t1 = status === GraphQLVolunteerStatus.Approved ? t("volunteers:confirmDisabledTooltip.outsideWindow") : t("volunteers:confirmDisabledTooltip.pendingApproval");
    $[0] = status;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const message = t1;
  let t2;
  if ($[3] !== children) {
    t2 = <TooltipTrigger asChild={true}><span className="inline-flex">{children}</span></TooltipTrigger>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== message) {
    t3 = <TooltipContent align="start" side="top" size="sm"><p>{message}</p></TooltipContent>;
    $[5] = message;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <Tooltip>{t2}{t3}</Tooltip>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
interface VolunteerSlotCardShiftDetailProps extends PropsWithChildren {
  iconName: IconNames;
  label: string;
}
const VolunteerSlotCardShiftDetail: React.FC<VolunteerSlotCardShiftDetailProps> = t0 => {
  const $ = _c(8);
  const {
    iconName,
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== iconName || $[1] !== label) {
    t1 = <dt className="flex label-neutral-subtle"><ArkIcon iconName={iconName} size="20" alt={label} /></dt>;
    $[0] = iconName;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children) {
    t2 = <dd className="text-wrap">{children}</dd>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex items-center gap-snug-xl">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const VolunteerSlotCard: React.FC<VolunteerSlotCardProps> = t0 => {
  const $ = _c(54);
  const {
    startTime,
    endTime,
    title,
    status,
    contacts,
    className,
    onCancel,
    onConfirm
  } = t0;
  const {
    i18n,
    t
  } = useTranslation("volunteers");
  let T0;
  let isCancelHidden;
  let isConfirmDisabled;
  let isConfirmHidden;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== contacts || $[2] !== endTime || $[3] !== i18n.language || $[4] !== i18n.resolvedLanguage || $[5] !== startTime || $[6] !== status || $[7] !== t || $[8] !== title) {
    const {
      badgeColor,
      contactList,
      dateTime,
      formattedDate,
      isCancelHidden: t10,
      isConfirmDisabled: t11,
      isConfirmHidden: t12,
      timeRange
    } = getVolunteerSlotCardData({
      contacts,
      endTime,
      language: i18n.resolvedLanguage ?? i18n.language,
      startTime,
      status
    });
    isCancelHidden = t10;
    isConfirmDisabled = t11;
    isConfirmHidden = t12;
    if ($[22] !== className) {
      t7 = cn("VolunteerSlotCard flex flex-col max-w-sm rounded-md p-comfy-lg gap-comfy-xs", className);
      $[22] = className;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t13;
    if ($[24] !== title) {
      t13 = <Text asChild={true} variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent}><h3 className="label-neutral-mild m-none">{title}</h3></Text>;
      $[24] = title;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    t8 = <div className="flex items-start justify-between gap-comfy-sm"><div className="grid min-w-0 gap-snug-md">{timeRange && <Text asChild={true} variant={Variants.Heading} size={Sizes.XSmall} prominence={Prominence.Amplified}><time className="VolunteerSlotCard__heading" dateTime={dateTime}>{timeRange}</time></Text>}{t13}</div>{badgeColor && status && <Badge color={badgeColor} label={t(`volunteers:statuses.${status}`)} size="sm" />}</div>;
    let t14;
    if ($[26] !== t) {
      t14 = t("volunteers:slotCard.shiftDetails.title");
      $[26] = t;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    if ($[28] !== t14) {
      t9 = <Text asChild={true} variant={Variants.Label} size={Sizes.Large}><h4 className="label-neutral-mild">{t14}</h4></Text>;
      $[28] = t14;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    T0 = Text;
    t4 = Variants.Label;
    t5 = Prominence.Subtle;
    t6 = true;
    t1 = "grid label-neutral-mild gap-snug-xl";
    t2 = startTime && <VolunteerSlotCardShiftDetail iconName="calendar" label={t("volunteers:slotCard.shiftDetails.dateLabel")}>{formattedDate}</VolunteerSlotCardShiftDetail>;
    t3 = contactList.length > 0 && <VolunteerSlotCardShiftDetail iconName="users-check" label={t("volunteers:slotCard.shiftDetails.contactsLabel")}><span className="flex flex-wrap">{contactList.map(_temp)}</span></VolunteerSlotCardShiftDetail>;
    $[0] = className;
    $[1] = contacts;
    $[2] = endTime;
    $[3] = i18n.language;
    $[4] = i18n.resolvedLanguage;
    $[5] = startTime;
    $[6] = status;
    $[7] = t;
    $[8] = title;
    $[9] = T0;
    $[10] = isCancelHidden;
    $[11] = isConfirmDisabled;
    $[12] = isConfirmHidden;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[9];
    isCancelHidden = $[10];
    isConfirmDisabled = $[11];
    isConfirmHidden = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t10;
  if ($[30] !== t1 || $[31] !== t2 || $[32] !== t3) {
    t10 = <dl className={t1}>{t2}{t3}</dl>;
    $[30] = t1;
    $[31] = t2;
    $[32] = t3;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== T0 || $[35] !== t10 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6) {
    t11 = <T0 variant={t4} prominence={t5} asChild={t6}>{t10}</T0>;
    $[34] = T0;
    $[35] = t10;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== isCancelHidden || $[41] !== isConfirmDisabled || $[42] !== isConfirmHidden || $[43] !== onCancel || $[44] !== onConfirm || $[45] !== status || $[46] !== t) {
    t12 = (!isConfirmHidden || !isCancelHidden) && (onConfirm || onCancel) && <div className="flex items-center mt-comfy-xs gap-comfy-sm">{!isConfirmHidden && onConfirm && <ConfirmTooltip disabled={!isConfirmDisabled} status={status}><ButtonStyle disabled={isConfirmDisabled} size="md"><button type="button" onClick={onConfirm}>{t("volunteers:slotCard.actions.confirm")}</button></ButtonStyle></ConfirmTooltip>}{!isCancelHidden && onCancel && <LinkStyle noUnderline={true} purpose="negative" size="md" noPadding={true}><button type="button" onClick={onCancel}>{t("volunteers:slotCard.actions.cancel")}</button></LinkStyle>}</div>;
    $[40] = isCancelHidden;
    $[41] = isConfirmDisabled;
    $[42] = isConfirmHidden;
    $[43] = onCancel;
    $[44] = onConfirm;
    $[45] = status;
    $[46] = t;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t11 || $[49] !== t12 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t13 = <div className={t7}>{t8}{t9}{t11}{t12}</div>;
    $[48] = t11;
    $[49] = t12;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  return t13;
};
export const VolunteerSlotCardSkeleton: React.FC<{
  className?: string;
}> = t0 => {
  const $ = _c(10);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("VolunteerSlotCard grid max-w-sm rounded-md p-comfy-lg gap-comfy-xs @container", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-start justify-between gap-comfy-sm"><div className="grid min-w-0 flex-1 gap-snug-lg"><Skeleton width="8rem" height={24} /><Skeleton width="12rem" height={20} /></div><Skeleton width="20cqw" height={21} borderRadius="var(--outline-radius-lg)" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="mb-snug-md" width="8rem" height={20} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-snug-xl"><Skeleton width={24} height={20} /><Skeleton width="13rem" height={20} /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center gap-snug-xl"><Skeleton width={24} height={20} /><Skeleton width="10rem" height={20} /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="grid gap-snug-xl">{t3}<div className="grid gap-snug-lg">{t4}{t5}<div className="flex items-center gap-snug-xl"><Skeleton width={24} height={20} /><Skeleton width="12rem" height={20} /></div></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center mt-snug-md gap-snug-lg"><Skeleton width={86} height={42} borderRadius={8} /><Skeleton width={78} height={42} borderRadius={8} /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t1) {
    t8 = <div className={t1}>{t2}{t6}{t7}</div>;
    $[8] = t1;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
};
function _temp(contact, idx) {
  return <span key={contact.name} className="inline-flex items-center gap-snug-md">{idx > 0 && <span aria-hidden="true">,</span>}<span>{contact.name}</span></span>;
}